import React from "react";
import ScheduleType from "../ScheduleType";
import {
  countConsultantDayStats,
  getConsultantInitials,
  resolveScheduleCell,
} from "../scheduleGridModel";
import type { ScheduleGridSlot } from "../scheduleGridTypes";
import { isoDateToDisplayDate } from "../agendaDateFormat";
import { SCHEDULE_GRID_CELL_HEIGHT_PX } from "../scheduleGridTheme";
import SlotCell from "./SlotCell";
import { findHourlyRainForSlot } from "../agendaWeatherHour";
import { WEATHER_ALERT_RAIN_THRESHOLD } from "../weatherConditionUi";

type ConsultantRowProps = {
  consultant: { id: number; first_name: string; city: string };
  dateIso: string;
  businessHours: string[];
  schedules: ScheduleGridSlot[];
  currentHourKey: string;
  isTodayInFortaleza: boolean;
  dateSelected: string[];
  canManageSchedules: boolean;
  initialStatus: number | undefined;
  onSelectCell: (
    scheduleOption: { id: number; date: string; status: number },
    event: React.MouseEvent
  ) => void;
  onSelectCellMouseOver: (coord: string, event: React.MouseEvent) => void;
  onOpenVisitDetails: (coord: string) => void;
  highlightedScheduleIds?: Set<number>;
  cityName?: string;
  hourRainByCity?: Map<string, Map<string, number>>;
};

const ConsultantRow: React.FC<ConsultantRowProps> = ({
  consultant,
  dateIso,
  businessHours,
  schedules,
  currentHourKey,
  isTodayInFortaleza,
  dateSelected,
  canManageSchedules,
  initialStatus,
  onSelectCell,
  onSelectCellMouseOver,
  onOpenVisitDetails,
  highlightedScheduleIds,
  cityName,
  hourRainByCity,
}) => {
  const displayDate = isoDateToDisplayDate(dateIso);
  const stats = countConsultantDayStats(
    schedules,
    displayDate,
    consultant.id,
    businessHours
  );

  return (
    <>
      <div
        className="sticky left-0 z-[2] flex items-center gap-2 border-r border-[#EDF2F7] bg-[#F7FAFC] pl-2 pr-2"
        style={{ height: SCHEDULE_GRID_CELL_HEIGHT_PX }}
        title={consultant.first_name}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FFF5F0] text-[10px] font-bold text-[#c4552b] ring-1 ring-[#F3D4C5]">
          {getConsultantInitials(consultant.first_name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-tight text-[#2D3748]">
            {consultant.first_name}
          </p>
          <p className="truncate text-xs leading-tight text-[#718096]">
            {stats.visits} visitas · {stats.freeSlots} livres
          </p>
        </div>
      </div>

      {businessHours.map((hour) => {
        const cell = resolveScheduleCell({
          dateDdMmYyyy: displayDate,
          hour,
          consultantId: consultant.id,
          schedules,
        });

        const hourKey = hour.split(":")[0];
        const currentKey = currentHourKey.split(":")[0];
        const isCurrentHour = isTodayInFortaleza && hourKey === currentKey;
        const isSelected = dateSelected.indexOf(cell.coord) >= 0;
        const scheduleOption = {
          id: cell.primarySlotId,
          date: cell.coord,
          status: cell.displayStatus,
        };
        const canDragSelect =
          canManageSchedules && cell.displayStatus !== ScheduleType.SCHEDULED;

        const rainChance =
          cityName && hourRainByCity
            ? findHourlyRainForSlot(hourRainByCity, cityName, hour)
            : null;
        const isWeatherHighlighted =
          highlightedScheduleIds?.has(cell.primarySlotId) ?? false;
        const isWeatherRiskHour =
          rainChance != null && rainChance >= WEATHER_ALERT_RAIN_THRESHOLD;

        return (
          <SlotCell
            key={cell.coord}
            cell={cell}
            isSelected={isSelected}
            isCurrentHour={isCurrentHour}
            isWeatherHighlighted={isWeatherHighlighted}
            isWeatherRiskHour={isWeatherRiskHour}
            canInteract={canDragSelect}
            onMouseDown={(event) => onSelectCell(scheduleOption, event)}
            onMouseOver={
              canDragSelect &&
              cell.displayStatus === initialStatus &&
              initialStatus !== ScheduleType.SCHEDULED
                ? (event) => onSelectCellMouseOver(cell.coord, event)
                : undefined
            }
            onOpenDetails={() => onOpenVisitDetails(cell.coord)}
          />
        );
      })}
    </>
  );
};

export default ConsultantRow;
